// custom hook to fetch dashboard insights data

import { useState, useEffect } from "react";
import { useGlobalContext } from "../../context/global";
import axios from "axios";
import { toast } from "react-toastify";
import { formatDate, getComparisonFormatTimeDimension } from "../../components/Reports/Components/utils";
import {
  PlatformWiseKeyMapper,
  PlatformWiseQueryMeasures,
  PlatformWiseQueryDefaultDimension,
  PlatformWiseQueryOrder,
} from "../../components/Filter/FilterData/quadrantFilter";
import {
  defaultDimensions,
  ruleItemInjector,
} from "../../helper/Quadrant/quadrantHelper";
import { isPlatformMatch } from "../../helper/Reports/common";
import { PerformanceAnalysisDefaultDimension, PerformanceAnalysisMeasures } from "../PerformanceAnalysis/performanceAnalysisConfigs";
import { getDashboardColumnsWithoutDimensions } from "../../components/Filter/FilterData/columnConfig";
import {
  AMAZON_OVERVIEW_DIMENSIONS,
  AMAZON_OVERVIEW_MEASURES,
} from "./OverviewCardSection/amazonOverviewConfig";

// Helper functions to extract product data based on platform
const getProductName = (product, userPlatform) => {
  if (userPlatform === "amazon") {
    return (
      product["VariantAnalysisTablePPMPlaceholder.name"] ||
      product["VariantAnalysisTablePPMPlaceholder.asin"] ||
      "Unknown Product"
    );
  } else {
    return product.product_name || product.product_id || "Unknown Product";
  }
};

const getSpends = (product, userPlatform) => {
  if (userPlatform === "amazon") {
    return product["VariantAnalysisTablePPMPlaceholder.cost"] || 0;
  } else {
    return product.spends || 0;
  }
};

const getRoas = (product, userPlatform) => {
  if (userPlatform === "amazon") {
    return product["VariantAnalysisTablePPMPlaceholder.roas"] || 0;
  } else {
    return product.roas || 0;
  }
};

const getRevenue = (product, userPlatform) => {
  if (userPlatform === "amazon") {
    return product["VariantAnalysisTablePPMPlaceholder.sales14d"] || 0;
  } else {
    return product.revenue || 0;
  }
};


// Get metric member name based on platform
const getSpendsMember = (userPlatform) => {
  if (userPlatform === "amazon") {
    return "VariantAnalysisTablePPMPlaceholder.cost";
  } else {
    return "spends";
  }
};

const getRoasMember = (userPlatform) => {
  if (userPlatform === "amazon") {
    return "VariantAnalysisTablePPMPlaceholder.roas";
  } else {
    return "roas";
  }
};

const getRevenueMember = (userPlatform) => {
  if (userPlatform === "amazon") {
    return "VariantAnalysisTablePPMPlaceholder.sales14d";
  } else {
    return "revenue";
  }
};

const getOrdersMember = (userPlatform) => {
  if (userPlatform === "amazon") {
    return "VariantAnalysisTablePPMPlaceholder.conversions14d";
  } else {
    return "orders";
  }
};

export const useProductInsights = () => {
  const userPlatform = JSON.parse(localStorage.getItem("user_platform")) || "amazon";
  const { calendarDate,applyCalendar, clientId, clientAccountId } = useGlobalContext();
  
  const [topPerformers, setTopPerformers] = useState({ data: [], summary: { totalSpends: 0, avgRoas: 0 } });
  const [highPotential, setHighPotential] = useState({ data: [], summary: { totalSpends: 0, avgRoas: 0 } });
  const [deficitProducts, setDeficitProducts] = useState({
    zeroRevenue: { data: [], summary: { totalSpends: 0, totalRevenue: 0 } },
    zeroSpends: { data: [], summary: { totalRevenue: 0, totalSpends: 0 } },
  });
  const [loadingState, setLoadingState] = useState({
    topPerformers: false,
    highPotential: false,
    deficitProducts: false,
  });
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    const fetchProductInsights = async () => {
      if (!clientId || !calendarDate?.[0]) return;

      try {
        // Set loading states
        setLoadingState({
          topPerformers: true,
          highPotential: true,
          deficitProducts: true,
        });
        setError(null);

        // Prepare time dimensions
        const timeDimensions = [
          {
            dimension: PlatformWiseKeyMapper[userPlatform]?.date,
            dateRange: [
              formatDate(calendarDate[0].startDate),
              formatDate(calendarDate[0].endDate),
            ],
          },
        ];

        // Get default dimensions
        const dimensions = PlatformWiseQueryDefaultDimension[userPlatform] || [];
        
        // Get measures
        const measures = PlatformWiseQueryMeasures[userPlatform] || [];

        // Prepare base filters with default dimensions
        let baseFilters = {};
        // Prepare base query data for summary
        const baseQueryData = {
          dimensions: dimensions,
          filters: baseFilters,
          extra_filters: {
            type: `${userPlatform}`,
            [PlatformWiseKeyMapper[userPlatform]?.defaultCampaignBackend]:
              baseFilters?.campaigns,
          },
          percentile_filters: [],
          measures: [
            ...measures,
            ...(isPlatformMatch(["amazon"]) ? [] : dimensions),
          ],
          order: PlatformWiseQueryOrder[userPlatform],
          timeDimensions: timeDimensions,
          limit: 50,
        };

        // Step 1: Fetch summary data to calculate avgSpends and performanceRoas
        const summaryUrl = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}/products:insights-summary?source=${userPlatform}`;
        const summaryResponse = await axios.post(summaryUrl, { query: baseQueryData });

        if (summaryResponse?.status !== 200) {
          throw new Error("Failed to fetch summary data");
        }

        const summary = summaryResponse.data;
        setSummaryData(summary);

        // Calculate avgSpends and performanceRoas from summary
        const spendsMember = getSpendsMember(userPlatform);
        const roasMember = getRoasMember(userPlatform);
        const ordersMember = getOrdersMember(userPlatform);

        const totalSpends = summary[spendsMember] || 0;
        const totalRoas = summary[roasMember] || 0;
        const totalOrders = summary[ordersMember] || 0;

        // avgSpends = 1.5 * (spends/orders)
        const avgSpends = totalOrders > 0 ? 1.5 * (totalSpends / totalOrders) : 0;
        
        // performanceRoas = roas + 30% of roas = roas * 1.3
        const performanceRoas = totalRoas * 1.3;

        // Helper function to create filter with metric conditions using ruleItemInjector
        const createMetricFilter = (spendsCondition, roasCondition, revenueCondition = null) => {
          const metricRules = [];

          if (spendsCondition) {
            metricRules.push({
              type: "RULE_ITEM",
              member: spendsMember,
              operator: spendsCondition.operator, // "gt", "lt", or "equals"
              values: [spendsCondition.value.toString()],
            });
          }

          if (roasCondition) {
            metricRules.push({
              type: "RULE_ITEM",
              member: roasMember,
              operator: roasCondition.operator, // "gt" or "lt"
              values: [roasCondition.value.toString()],
            });
          }

          if (revenueCondition) {
            metricRules.push({
              type: "RULE_ITEM",
              member: getRevenueMember(userPlatform),
              operator: revenueCondition.operator, // "gt", "lt", or "equals"
              values: [revenueCondition.value.toString()],
            });
          }

          if (metricRules.length === 0) return baseFilters;

          // Use ruleItemInjector to properly inject metric rules
          // Create a copy of baseFilters to avoid mutating the original
          let filters = JSON.parse(JSON.stringify(baseFilters));
          
          // Use ruleItemInjector with "measures" key (API uses "measures" for metrics) and AND_RULE type
          filters = ruleItemInjector(filters, "measures", metricRules, "AND_RULE");

          return filters;
        };

        // Helper function to fetch products with filters
        const fetchProductsWithFilters = async (filters, order, limit) => {
          const queryData = {
            ...baseQueryData,
            filters: filters,
            order: order,
            limit: limit,
          };

          const dataUrl = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}/products:insights?source=${userPlatform}`;
          const response = await axios.post(dataUrl, { query: queryData });

          if (response?.status !== 200) {
            throw new Error("Failed to fetch products data");
          }

          return response.data?.data || [];
        };

        // Helper function to fetch summary with filters
        const fetchSummaryWithFilters = async (filters) => {
          const queryData = {
            ...baseQueryData,
            filters: filters,
            limit: 50,
          };

          const summaryUrl = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}/products:insights-summary?source=${userPlatform}`;
          const response = await axios.post(summaryUrl, { query: queryData });

          if (response?.status !== 200) {
            throw new Error("Failed to fetch summary data");
          }

          return response.data || {};
        };

        // Step 2: Prepare filters for all sections
        const topPerformersFilters = createMetricFilter(
          { operator: "gt", value: avgSpends },
          { operator: "gt", value: performanceRoas }
        );
        const topPerformersOrder = userPlatform === "amazon" 
          ? [["VariantAnalysisTablePPMPlaceholder.cost", "desc"]]
          : [["spends", "desc"]];

        const highPotentialFilters = createMetricFilter(
          { operator: "lt", value: avgSpends },
          { operator: "gt", value: performanceRoas }
        );
        const highPotentialOrder = userPlatform === "amazon"
          ? [["VariantAnalysisTablePPMPlaceholder.cost", "desc"]]
          : [["spends", "desc"]];

        const zeroRevenueFilters = createMetricFilter(
          null,
          null,
          { operator: "equals", value: 0 }
        );
        const zeroRevenueOrder = userPlatform === "amazon"
          ? [["VariantAnalysisTablePPMPlaceholder.cost", "desc"]]
          : [["spends", "desc"]];

        const zeroSpendsFilters = createMetricFilter(
          { operator: "equals", value: 0 },
          null,
          null
        );
        const zeroSpendsOrder = userPlatform === "amazon"
          ? [["VariantAnalysisTablePPMPlaceholder.sales14d", "desc"]]
          : [["revenue", "desc"]];

        const revenueMember = getRevenueMember(userPlatform);

        // Step 3: Fetch all data in parallel (products and summaries for each section)
        const [
          topPerformersProducts,
          topPerformersSummaryData,
          highPotentialProducts,
          highPotentialSummaryData,
          zeroRevenueProducts,
          zeroRevenueSummaryData,
          zeroSpendsProducts,
          zeroSpendsSummaryData,
        ] = await Promise.all([
          // Top Performers
          fetchProductsWithFilters(topPerformersFilters, topPerformersOrder, 5),
          fetchSummaryWithFilters(topPerformersFilters),
          // High Potential
          fetchProductsWithFilters(highPotentialFilters, highPotentialOrder, 5),
          fetchSummaryWithFilters(highPotentialFilters),
          // Deficit Products - Zero Revenue
          fetchProductsWithFilters(zeroRevenueFilters, zeroRevenueOrder, 4),
          fetchSummaryWithFilters(zeroRevenueFilters),
          // Deficit Products - Zero Spends
          fetchProductsWithFilters(zeroSpendsFilters, zeroSpendsOrder, 4),
          fetchSummaryWithFilters(zeroSpendsFilters),
        ]);

        // Step 4: Process Top Performers data
        const topPerformersList = topPerformersProducts.map((product) => ({
          name: getProductName(product, userPlatform),
          spend: getSpends(product, userPlatform),
          roas: getRoas(product, userPlatform),
          rawSpends: getSpends(product, userPlatform),
          rawRoas: getRoas(product, userPlatform),
        }));

        const topPerformersSummary = {
          totalSpends: topPerformersSummaryData[spendsMember] || 0,
          avgRoas: topPerformersSummaryData[roasMember] || 0,
        };

        setTopPerformers({
          data: topPerformersList,
          summary: topPerformersSummary,
          filters: topPerformersFilters,
        });
        setLoadingState((prev) => ({ ...prev, topPerformers: false }));

        // Step 5: Process High Potential data
        const highPotentialList = highPotentialProducts.map((product) => ({
          name: getProductName(product, userPlatform),
          spend: getSpends(product, userPlatform),
          roas: getRoas(product, userPlatform),
          rawSpends: getSpends(product, userPlatform),
          rawRoas: getRoas(product, userPlatform),
        }));

        const highPotentialSummary = {
          totalSpends: highPotentialSummaryData[spendsMember] || 0,
          avgRoas: highPotentialSummaryData[roasMember] || 0,
        };

        setHighPotential({
          data: highPotentialList,
          summary: highPotentialSummary,
          filters: highPotentialFilters,
        });
        setLoadingState((prev) => ({ ...prev, highPotential: false }));

        // Step 6: Process Deficit Products data
        const zeroRevenueList = zeroRevenueProducts.map((product) => ({
          name: getProductName(product, userPlatform),
          spend: getSpends(product, userPlatform),
          revenue: getRevenue(product, userPlatform),
          rawSpends: getSpends(product, userPlatform),
          rawRevenue: getRevenue(product, userPlatform),
        }));

        const zeroRevenueSummary = {
          totalSpends: zeroRevenueSummaryData[spendsMember] || 0,
          totalRevenue: zeroRevenueSummaryData[revenueMember] || 0,
        };

        const zeroSpendsList = zeroSpendsProducts.map((product) => ({
          name: getProductName(product, userPlatform),
          spend: getSpends(product, userPlatform),
          revenue: getRevenue(product, userPlatform),
          rawRevenue: getRevenue(product, userPlatform),
        }));

        const deficitSummary = {
          zeroRevenue: zeroRevenueSummary,
          zeroSpends: {
            totalRevenue: zeroSpendsSummaryData[revenueMember] || 0,
            totalSpends: zeroSpendsSummaryData[spendsMember] || 0,
          },
        };

        setDeficitProducts({
          zeroRevenue: {
            data: zeroRevenueList,
            summary: deficitSummary.zeroRevenue,
            filters: zeroRevenueFilters,
          },
          zeroSpends: {
            data: zeroSpendsList,
            summary: deficitSummary.zeroSpends,
            filters: zeroSpendsFilters,
          },
        });
        setLoadingState((prev) => ({ ...prev, deficitProducts: false }));

      } catch (err) {
        console.error("Error fetching product insights:", err);
        setError(err?.response?.data?.message || err?.message || "Unknown error occurred");
        toast.error(err?.response?.data?.message || err?.message || "Failed to fetch product insights");
        setLoadingState({
          topPerformers: false,
          highPotential: false,
          deficitProducts: false,
        });
      }
    };

    fetchProductInsights();
  }, [clientId, userPlatform, clientAccountId, applyCalendar]);

  return {
    topPerformers,
    highPotential,
    deficitProducts,
    loadingState,
    error,
    summaryData,
  };
};



export const useDashboardOverview = (enabled = true) => {
  const userPlatform = JSON.parse(localStorage.getItem("user_platform")) || "amazon";
  const { calendarDate,applyCalendar, clientId, clientAccountId } = useGlobalContext();
  
  const [overviewData, setOverviewData] = useState({
    current_data: {},
    comparison_data: {},
    trend_data_series: {},
    change_percentage_data: {},
    total_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardOverview = async () => {
      if (!enabled || !clientId || !calendarDate?.[0]) return;

      try {
        setLoading(true);
        setError(null);

        // Get startDate and endDate from calendarDate
        const startDate = formatDate(calendarDate[0].startDate);
        const endDate = formatDate(calendarDate[0].endDate);

        // Calculate the gap (difference in days)
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        const gapInMs = endDateObj - startDateObj;
        const gapInDays = Math.round(gapInMs / (1000 * 60 * 60 * 24));

        // Calculate previous date range: [startDate - gap, startDate]
        const previousStartDateObj = new Date(startDateObj);
        previousStartDateObj.setDate(previousStartDateObj.getDate() - gapInDays);
        const previousStartDate = formatDate(previousStartDateObj);
        const previousEndDate = startDate; // Previous period ends where current period starts
        

        // Prepare time dimensions with compareDateRange
        const timeDimensions = [
          {
            dimension: PlatformWiseKeyMapper[userPlatform]?.date,
            compareDateRange: [
              [startDate, endDate],
              [previousStartDate, previousEndDate],
            ],
          },
        ];
        // Get default dimensions and measures
        const dimensions = PerformanceAnalysisDefaultDimension[userPlatform] || [];
        const measures = PerformanceAnalysisMeasures[userPlatform] || [];
        // Prepare base filters with default dimensions
        let baseFilters = {};
        // Prepare query payload for placement API
        const queryData = {
          dimensions,
          filters: baseFilters,
          extra_filters: {
            type: `${userPlatform}`,
            [PlatformWiseKeyMapper[userPlatform]?.defaultCampaignBackend]: baseFilters?.campaigns,
          },
          percentile_filters: [],
          measures : [...measures, ...(isPlatformMatch(["amazon"]) ? [] : dimensions)],
          order: [],
          timeDimensions: getComparisonFormatTimeDimension(timeDimensions),
        };

        // Prepare query payload for products API to fetch missing metrics
        // Get all product measures and filter only the ones we need
        const allProductMeasures = PlatformWiseQueryMeasures[userPlatform] || [];
        const productMeasures = ["gmv", "order_units", "total_cvr", "total_roas"].filter(measure => 
          allProductMeasures.includes(measure)
        );
        const productDimensions = PlatformWiseQueryDefaultDimension[userPlatform] || [];
        
        // Prepare base filters for products API with default dimensions
        let productBaseFilters = {};
        if (userPlatform === 'amazon') {
          productBaseFilters = ruleItemInjector(
            productBaseFilters,
            "dimensions",
            defaultDimensions(clientAccountId)
          );
        }

        const productQueryData = {
          dimensions: productDimensions,
          filters: productBaseFilters,
          extra_filters: {
            type: `${userPlatform}`,
            [PlatformWiseKeyMapper[userPlatform]?.defaultCampaignBackend]: productBaseFilters?.campaigns,
          },
          percentile_filters: [],
          measures: [...productMeasures, ...(isPlatformMatch(["amazon"]) ? [] : productDimensions)],
          order: [],
          timeDimensions: getComparisonFormatTimeDimension(timeDimensions),
        };

        // Fetch comparison summary from both APIs in parallel
        const summaryUrl = `${process.env.REACT_APP_REPORT_API}/client/${clientId}/placement:comparison-summary?source=${userPlatform}`;
        const productSummaryUrl = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}/products:comparison-summary?source=${userPlatform}`;
        
        const [placementResponse, productResponse] = await Promise.allSettled([
          axios.post(summaryUrl, { query: queryData }),
          axios.post(productSummaryUrl, { query: productQueryData }),
        ]);

        // Process placement response
        let placementData = {};
        if (placementResponse.status === 'fulfilled' && placementResponse.value?.status === 200) {
          placementData = placementResponse.value.data;
        }

        // Process products response (can fail gracefully)
        let productData = {};
        if (productResponse.status === 'fulfilled' && productResponse.value?.status === 200) {
          productData = productResponse.value.data;
        } else if (productResponse.status === 'rejected') {
          // If products API fails, log error but continue with placement data
          console.warn("Error fetching products comparison summary:", productResponse.reason);
        }

        // Merge data: take placement data as base and add product-specific metrics
          const mergeData = (placementObj, productObj) => {
            if (!placementObj || !productObj) return placementObj || {};
            return {
              ...placementObj,
              ...(productObj.gmv !== undefined && { gmv: productObj.gmv }),
              ...(productObj.order_units !== undefined && { order_units: productObj.order_units }),
              ...(productObj.total_cvr !== undefined && { total_cvr: productObj.total_cvr }),
              ...(productObj.total_roas !== undefined && { total_roas: productObj.total_roas }),
            };
          };

          // Merge change percentage data from both APIs
          const placementChangePercentage = placementData?.change_percentage_data || {};
          const productChangePercentage = productData?.change_percentage_data || {};
          const mergedChangePercentage = {
            ...placementChangePercentage,
            ...(productChangePercentage.gmv !== undefined && { gmv: productChangePercentage.gmv }),
            ...(productChangePercentage.order_units !== undefined && { order_units: productChangePercentage.order_units }),
            ...(productChangePercentage.total_cvr !== undefined && { total_cvr: productChangePercentage.total_cvr }),
            ...(productChangePercentage.total_roas !== undefined && { total_roas: productChangePercentage.total_roas }),
          };

          // The comparison-summary API returns data in a specific format
          // It may return the data directly or nested, so we handle both cases
          const overviewData = {
            current_data: mergeData(
              placementData?.current_data || {},
              productData?.current_data || {}
            ),
            comparison_data: mergeData(
              placementData?.comparison_data || {},
              productData?.comparison_data || {}
            ),
            change_percentage_data: mergedChangePercentage,
            total_count: placementData?.total_count || 0,
          };

        if (placementResponse.status === 'fulfilled' && placementResponse.value?.status === 200) {
          setOverviewData(overviewData);
        }
      } catch (err) {
        console.error("Error fetching dashboard overview:", err);
        setError(err?.response?.data?.message || err?.message || "Unknown error occurred");
        toast.error(err?.response?.data?.message || err?.message || "Failed to fetch dashboard overview");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardOverview();
  }, [enabled, clientId, userPlatform, applyCalendar, clientAccountId]);

  return {
    overviewData,
    loading,
    error,
  };
};

/**
 * Amazon home dashboard overview cards — adset:comparison-summary wrapper API.
 * selectedPortfolios: portfolio names from MultiSelect (empty = all).
 */
export const useAmazonDashboardOverview = (
  selectedPortfolios = [],
  enabled = true
) => {
  const { calendarDate, applyCalendar, clientId } = useGlobalContext();
  const [overviewData, setOverviewData] = useState({
    current_data: {},
    comparison_data: {},
    change_percentage_data: {},
    total_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAmazonOverview = async () => {
      if (!enabled || !clientId || !calendarDate?.[0]) return;

      try {
        setLoading(true);
        setError(null);

        const startDate = formatDate(calendarDate[0].startDate);
        const endDate = formatDate(calendarDate[0].endDate);
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        const gapInDays = Math.round(
          (endDateObj - startDateObj) / (1000 * 60 * 60 * 24)
        );

        // Preceding period of equal length, non-overlapping (same pattern as wrapper curl).
        const previousEndDateObj = new Date(startDateObj);
        previousEndDateObj.setDate(previousEndDateObj.getDate() - 1);
        const previousStartDateObj = new Date(previousEndDateObj);
        previousStartDateObj.setDate(
          previousStartDateObj.getDate() - gapInDays
        );
        const previousStartDate = formatDate(previousStartDateObj);
        const previousEndDate = formatDate(previousEndDateObj);

        const timeDimensions = [
          {
            dimension: "date",
            compareDateRange: [
              [startDate, endDate],
              [previousStartDate, previousEndDate],
            ],
          },
        ];

        let filters = {};
        if (selectedPortfolios?.length) {
          filters = ruleItemInjector(
            {},
            "dimensions",
            [
              {
                type: "RULE_ITEM",
                member: "portfolio_name",
                operator: "in",
                values: selectedPortfolios,
              },
            ],
            "AND_RULE"
          );
        }

        const queryData = {
          dimensions: AMAZON_OVERVIEW_DIMENSIONS,
          filters,
          measures: AMAZON_OVERVIEW_MEASURES,
          order: [["spends", "desc"]],
          timeDimensions: getComparisonFormatTimeDimension(timeDimensions),
          offset: 0,
          limit: 50,
        };

        const summaryUrl = `${process.env.REACT_APP_REPORT_API}/client/${clientId}/adset:comparison-summary?source=amazon`;
        const response = await axios.post(summaryUrl, { query: queryData });

        if (response?.status === 200) {
          const data = response.data || {};
          setOverviewData({
            current_data: data?.current_data || {},
            comparison_data: data?.comparison_data || {},
            change_percentage_data: data?.change_percentage_data || {},
            total_count: data?.total_count || 0,
          });
        }
      } catch (err) {
        console.error("Error fetching Amazon dashboard overview:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unknown error occurred"
        );
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch dashboard overview"
        );
        setOverviewData({
          current_data: {},
          comparison_data: {},
          change_percentage_data: {},
          total_count: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAmazonOverview();
  }, [enabled, clientId, applyCalendar, selectedPortfolios]);

  return {
    overviewData,
    loading,
    error,
  };
};

const buildAmazonPortfolioFilters = (selectedPortfolios = []) => {
  if (!selectedPortfolios?.length) return {};
  return ruleItemInjector(
    {},
    "dimensions",
    [
      {
        type: "RULE_ITEM",
        member: "portfolio_name",
        operator: "in",
        values: selectedPortfolios,
      },
    ],
    "AND_RULE"
  );
};

/**
 * Amazon overview card sparklines — adset:insights-summary-trends
 * Same current/previous split logic as useDashboardOverviewChart.
 */
export const useAmazonDashboardOverviewChart = (
  selectedPortfolios = [],
  enabled = true
) => {
  const { calendarDate, applyCalendar, clientId } = useGlobalContext();
  const [trendDataSeries, setTrendDataSeries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrendDataSeries = async () => {
      if (!enabled || !clientId || !calendarDate?.[0]) return;

      try {
        setLoading(true);
        setError(null);

        const userStartDate = formatDate(calendarDate[0].startDate);
        const userEndDate = formatDate(calendarDate[0].endDate);
        const userStartDateObj = new Date(userStartDate);
        const userEndDateObj = new Date(userEndDate);
        const gapInDays = Math.round(
          (userEndDateObj - userStartDateObj) / (1000 * 60 * 60 * 24)
        );

        const backendStartDateObj = new Date(userStartDateObj);
        backendStartDateObj.setDate(backendStartDateObj.getDate() - gapInDays);
        const backendStartDate = formatDate(backendStartDateObj);
        const backendEndDate = userEndDate;

        const filters = buildAmazonPortfolioFilters(selectedPortfolios);
        const queryData = {
          dimensions: [],
          filters,
          extra_filters: { type: "amazon" },
          measures: AMAZON_OVERVIEW_MEASURES,
          order: [["spends", "desc"]],
          timeDimensions: [
            {
              dimension: "date",
              dateRange: [backendStartDate, backendEndDate],
            },
          ],
        };

        const dataUrl = `${process.env.REACT_APP_REPORT_API}/client/${clientId}/adset:insights-summary-trends?source=amazon`;
        const response = await axios.post(dataUrl, {
          filter: { query: queryData },
          split_by: ["date"],
        });

        if (response?.status === 200) {
          const data = response.data?.data || [];
          const dateField = "date";
          const currentData = [];
          const previousData = [];

          data.forEach((item) => {
            const itemDate = item[dateField] || item.date;
            if (!itemDate) return;

            try {
              const itemDateObj = new Date(
                typeof itemDate === "string" ? itemDate : itemDate.toString()
              );
              itemDateObj.setHours(0, 0, 0, 0);

              const userStartDateObjCheck = new Date(userStartDate);
              userStartDateObjCheck.setHours(0, 0, 0, 0);
              const userEndDateObjCheck = new Date(userEndDate);
              userEndDateObjCheck.setHours(23, 59, 59, 999);

              if (
                itemDateObj >= userStartDateObjCheck &&
                itemDateObj <= userEndDateObjCheck
              ) {
                currentData.push(item);
              } else if (itemDateObj < userStartDateObjCheck) {
                previousData.push(item);
              }
            } catch (e) {
              console.warn("Error parsing date:", itemDate, e);
            }
          });

          const sortByDate = (a, b) =>
            new Date(a[dateField] || a.date || 0) -
            new Date(b[dateField] || b.date || 0);
          currentData.sort(sortByDate);
          previousData.sort(sortByDate);

          const mappedTrendData = {};
          AMAZON_OVERVIEW_MEASURES.forEach((columnKey) => {
            mappedTrendData[columnKey] = [
              {
                name: "Current",
                data: currentData.map((item) =>
                  item[columnKey] != null ? Number(item[columnKey]) : 0
                ),
              },
              {
                name: "Previous",
                data: previousData.map((item) =>
                  item[columnKey] != null ? Number(item[columnKey]) : 0
                ),
              },
            ];
          });

          setTrendDataSeries(mappedTrendData);
        }
      } catch (err) {
        console.error("Error fetching Amazon overview chart:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unknown error occurred"
        );
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch trend data series"
        );
        setTrendDataSeries({});
      } finally {
        setLoading(false);
      }
    };

    fetchTrendDataSeries();
  }, [enabled, clientId, applyCalendar, selectedPortfolios]);

  return {
    chartData: trendDataSeries,
    loading,
    error,
  };
};

/**
 * Amazon Performance Analysis chart — adset:insights-summary-trends
 */
export const useAmazonPerformanceChart = (
  timePeriod,
  selectedPortfolios = [],
  enabled = true
) => {
  const { calendarDate, applyCalendar, clientId } = useGlobalContext();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPerformanceChartData = async () => {
      if (!enabled || !clientId || !calendarDate?.[0]) return;

      try {
        setLoading(true);
        setError(null);

        const filters = buildAmazonPortfolioFilters(selectedPortfolios);
        const queryData = {
          dimensions: [],
          filters,
          extra_filters: { type: "amazon" },
          measures: AMAZON_OVERVIEW_MEASURES,
          order: [[timePeriod, "asc"]],
          timeDimensions: [
            {
              dimension: "date",
              dateRange: [
                formatDate(calendarDate[0].startDate),
                formatDate(calendarDate[0].endDate),
              ],
            },
          ],
        };

        const dataUrl = `${process.env.REACT_APP_REPORT_API}/client/${clientId}/adset:insights-summary-trends?source=amazon`;
        const response = await axios.post(dataUrl, {
          filter: { query: queryData },
          split_by: [timePeriod],
        });

        if (response?.status === 200) {
          const data = response.data?.data || [];
          const dateField = timePeriod === "date" ? "date" : timePeriod;

          const transformedData = data.map((item) => {
            let dateLabel = "";
            if (timePeriod === "date") {
              dateLabel = item.date_formatted || item.date || "";
            } else if (timePeriod === "week") {
              dateLabel = item.week_formatted || item.week || "";
            } else if (timePeriod === "month") {
              dateLabel = item.month_formatted || item.month || "";
            }

            return {
              ...item,
              dateLabel,
              dateValue: item.date || item[timePeriod] || item[dateField] || "",
            };
          });

          transformedData.sort((a, b) => {
            try {
              return (
                new Date(a.dateValue || 0) - new Date(b.dateValue || 0)
              );
            } catch (e) {
              return 0;
            }
          });

          setChartData(transformedData);
        }
      } catch (err) {
        console.error("Error fetching Amazon performance chart:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unknown error occurred"
        );
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch performance chart data"
        );
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceChartData();
  }, [enabled, clientId, applyCalendar, selectedPortfolios, timePeriod]);

  return {
    chartData,
    loading,
    error,
  };
};

export const usePerformanceChart = (
  timePeriod,
  customCalendarDate = null,
  enabled = true
) => {
  const userPlatform = JSON.parse(localStorage.getItem("user_platform")) || "amazon";
  const { calendarDate: defaultCalendarDate, applyCalendar, clientId, clientAccountId } = useGlobalContext();
  const calendarDate = customCalendarDate || defaultCalendarDate;

  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPerformanceChartData = async () => {
      if (!enabled || !clientId || !calendarDate?.[0]) return;

      try {
        setLoading(true);
        setError(null);

        // Prepare time dimensions
        const timeDimensions = [
            {
              dimension: PlatformWiseKeyMapper[userPlatform]?.date,
              dateRange: [
                formatDate(calendarDate[0].startDate),
                formatDate(calendarDate[0].endDate),
              ],
            },
          ];

        // Get default measures for placement API
        const measures = PerformanceAnalysisMeasures[userPlatform] || [];

        // Prepare base filters for placement API
        let baseFilters = {};
        
        // Prepare query payload for placement API
        const queryData = {
          dimensions:[],
          filters: baseFilters,
          extra_filters: {
            type: `${userPlatform}`,
            [PlatformWiseKeyMapper[userPlatform]?.defaultCampaignBackend]: baseFilters?.campaigns,
          },
          percentile_filters: [],
          measures: measures,
          order: [[timePeriod, "asc"]],
          timeDimensions,
        };

        // Prepare query payload for products API to fetch missing metrics
        const allProductMeasures = PlatformWiseQueryMeasures[userPlatform] || [];
        const productMeasures = ["gmv", "order_units", "total_cvr", "total_roas"].filter(measure => 
          allProductMeasures.includes(measure)
        );
        const productDimensions = PlatformWiseQueryDefaultDimension[userPlatform] || [];
        
        // Prepare base filters for products API with default dimensions
        let productBaseFilters = {};
        if (userPlatform === 'amazon') {
          productBaseFilters = ruleItemInjector(
            productBaseFilters,
            "dimensions",
            defaultDimensions(clientAccountId)
          );
        }

        const productQueryData = {
          dimensions: productDimensions,
          filters: productBaseFilters,
          extra_filters: {
            type: `${userPlatform}`,
            [PlatformWiseKeyMapper[userPlatform]?.defaultCampaignBackend]: productBaseFilters?.campaigns,
          },
          percentile_filters: [],
          measures: [...productMeasures, ...(isPlatformMatch(["amazon"]) ? [] : productDimensions)],
          order: [[timePeriod, "asc"]],
          timeDimensions,
        };

        // Fetch trend data from both APIs in parallel
        const dataUrl = `${process.env.REACT_APP_REPORT_API}/client/${clientId}/placement:insights-summary-trends?source=${userPlatform}`;
        const productDataUrl = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}/products:insights-summary-trends?source=${userPlatform}`;
        
        const [placementResponse, productResponse] = await Promise.allSettled([
          axios.post(dataUrl, { filter:{query: queryData}, split_by: [timePeriod] }),
          axios.post(productDataUrl, {
            filter: { query: productQueryData },
            split_by: [timePeriod],
          }),
        ]);

        // Process placement response
        let placementData = [];
        if (placementResponse.status === 'fulfilled' && placementResponse.value?.status === 200) {
          placementData = placementResponse.value.data?.data || [];
        }

        // Process products response (can fail gracefully)
        let productData = [];
        if (productResponse.status === 'fulfilled' && productResponse.value?.status === 200) {
          productData = productResponse.value.data?.data || [];
        } else if (productResponse.status === 'rejected') {
          // If products API fails, log error but continue with placement data
          console.warn("Error fetching products trend data:", productResponse.reason);
        }

        if (placementResponse.status === 'fulfilled' && placementResponse.value?.status === 200) {
          
          // Determine the date field based on timePeriod
          const dateField = timePeriod === "date" 
            ? (PlatformWiseKeyMapper[userPlatform]?.date || "date")
            : timePeriod;

          // Merge placement and product data by date/week/month
          const dataMap = new Map();

          // First, add all placement data to the map
          placementData.forEach((item) => {
            const itemDate = item[dateField] || item[timePeriod] || item.date;
            if (itemDate) {
              dataMap.set(itemDate, { ...item });
            }
          });

          // Then, merge product data into the map
          productData.forEach((item) => {
            const itemDate = item[dateField] || item[timePeriod] || item.date;
            if (itemDate) {
              const existingItem = dataMap.get(itemDate) || {};
              // Add product-specific metrics
              if (item.gmv !== undefined) existingItem.gmv = item.gmv;
              if (item.order_units !== undefined) existingItem.order_units = item.order_units;
              if (item.total_cvr !== undefined) existingItem.total_cvr = item.total_cvr;
              if (item.total_roas !== undefined) existingItem.total_roas = item.total_roas;
              dataMap.set(itemDate, existingItem);
            }
          });

          // Convert map back to array
          const data = Array.from(dataMap.values());
          
          // Transform data for chart
          const transformedData = data.map((item) => {
            // Get the appropriate date label based on timePeriod
            let dateLabel = "";
            
            if (timePeriod === "date") {
              dateLabel = item.date_formatted || item.date || "";
            } else if (timePeriod === "week") {
              dateLabel = item.week_formatted || item.week || "";
            } else if (timePeriod === "month") {
              dateLabel = item.month_formatted || item.month || "";
            }

            return {
              ...item,
              dateLabel,
              dateValue: item.date || item[timePeriod] || "",
            };
          });

          // Sort by date to ensure proper order
          transformedData.sort((a, b) => {
            try {
              const dateA = new Date(a.dateValue || 0);
              const dateB = new Date(b.dateValue || 0);
              return dateA - dateB;
            } catch (e) {
              // If date parsing fails, maintain original order
              return 0;
            }
          });

          setChartData(transformedData);
        }
      } catch (err) {
        console.error("Error fetching performance chart data:", err);
        setError(err?.response?.data?.message || err?.message || "Unknown error occurred");
        toast.error(err?.response?.data?.message || err?.message || "Failed to fetch performance chart data");
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceChartData();
  }, [enabled, clientId, userPlatform, applyCalendar, clientAccountId, timePeriod]);

  return {
    chartData,
    loading,
    error,
  };
};

export const useDashboardOverviewChart = (enabled = true) => {
  const userPlatform = JSON.parse(localStorage.getItem("user_platform")) || "amazon";
  const { calendarDate, applyCalendar, clientId, clientAccountId } = useGlobalContext();

  const [trendDataSeries, setTrendDataSeries] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrendDataSeries = async () => {
      if (!enabled || !clientId || !calendarDate?.[0]) return;

      try {
        setLoading(true);
        setError(null);

        // Get user's selected startDate and endDate from calendarDate
        const userStartDate = formatDate(calendarDate[0].startDate);
        const userEndDate = formatDate(calendarDate[0].endDate);

        // Calculate the gap (difference in days)
        const userStartDateObj = new Date(userStartDate);
        const userEndDateObj = new Date(userEndDate);
        const gapInMs = userEndDateObj - userStartDateObj;
        const gapInDays = Math.round(gapInMs / (1000 * 60 * 60 * 24));

        // Calculate extended start date for backend: startDate - gap
        // Example: user selects 07-02-2025 to 14-02-2025
        // Backend should receive: 01-02-2025 to 14-02-2025
        const backendStartDateObj = new Date(userStartDateObj);
        backendStartDateObj.setDate(backendStartDateObj.getDate() - gapInDays);
        const backendStartDate = formatDate(backendStartDateObj);
        const backendEndDate = userEndDate; // Backend end date = user's end date

        // Prepare time dimensions - fetch data from extended start to end date
        // This will include both previous period (01-02 to 07-02) and current period (07-02 to 14-02)
        const timeDimensions = [
          {
            dimension: PlatformWiseKeyMapper[userPlatform]?.date,
            dateRange: [backendStartDate, backendEndDate],
          },
        ];

        // Get default measures for placement API
        const measures = PerformanceAnalysisMeasures[userPlatform] || [];

        // Prepare base filters for placement API
        let baseFilters = {};

        // Prepare query payload for placement API
        const queryData = {
          dimensions: [],
          filters: baseFilters,
          extra_filters: {
            type: `${userPlatform}`,
            [PlatformWiseKeyMapper[userPlatform]?.defaultCampaignBackend]: baseFilters?.campaigns,
          },
          percentile_filters: [],
          measures: measures,
          order: [[PlatformWiseKeyMapper[userPlatform]?.date || "date", "asc"]],
          timeDimensions,
        };

        // Prepare query payload for products API to fetch missing metrics
        const allProductMeasures = PlatformWiseQueryMeasures[userPlatform] || [];
        const productMeasures = ["gmv", "order_units", "total_cvr", "total_roas"].filter(measure => 
          allProductMeasures.includes(measure)
        );
        const productDimensions = PlatformWiseQueryDefaultDimension[userPlatform] || [];
        
        // Prepare base filters for products API with default dimensions
        let productBaseFilters = {};
        if (userPlatform === 'amazon') {
          productBaseFilters = ruleItemInjector(
            productBaseFilters,
            "dimensions",
            defaultDimensions(clientAccountId)
          );
        }

        const productQueryData = {
          dimensions: productDimensions,
          filters: productBaseFilters,
          extra_filters: {
            type: `${userPlatform}`,
            [PlatformWiseKeyMapper[userPlatform]?.defaultCampaignBackend]: productBaseFilters?.campaigns,
          },
          percentile_filters: [],
          measures: [...productMeasures, ...(isPlatformMatch(["amazon"]) ? [] : productDimensions)],
          order: [[PlatformWiseKeyMapper[userPlatform]?.date || "date", "asc"]],
          timeDimensions,
        };

        // Fetch trend data from both APIs in parallel - this will return data for both periods
        const dataUrl = `${process.env.REACT_APP_REPORT_API}/client/${clientId}/placement:insights-summary-trends?source=${userPlatform}`;
        const productDataUrl = `${process.env.REACT_APP_CATALOG_API}/client/${clientId}/products:insights-summary-trends?source=${userPlatform}`;
        
        const [placementResponse, productResponse] = await Promise.allSettled([
          axios.post(dataUrl, {
            filter: { query: queryData },
            split_by: ["date"],
          }),
          axios.post(productDataUrl, {
            filter: { query: productQueryData },
            split_by: ["date"],
          }),
        ]);

        // Process placement response
        let placementData = [];
        if (placementResponse.status === 'fulfilled' && placementResponse.value?.status === 200) {
          placementData = placementResponse.value.data?.data || [];
        }

        // Process products response (can fail gracefully)
        let productData = [];
        if (productResponse.status === 'fulfilled' && productResponse.value?.status === 200) {
          productData = productResponse.value.data?.data || [];
        } else if (productResponse.status === 'rejected') {
          // If products API fails, log error but continue with placement data
          console.warn("Error fetching products trend data:", productResponse.reason);
        }

        if (placementResponse.status === 'fulfilled' && placementResponse.value?.status === 200) {
          const dateField = PlatformWiseKeyMapper[userPlatform]?.date || "date";

          // Merge placement and product data by date
          const dataMap = new Map();

          // First, add all placement data to the map
          placementData.forEach((item) => {
            const itemDate = item[dateField] || item.date;
            if (itemDate) {
              dataMap.set(itemDate, { ...item });
            }
          });

          // Then, merge product data into the map
          productData.forEach((item) => {
            const itemDate = item[dateField] || item.date;
            if (itemDate) {
              const existingItem = dataMap.get(itemDate) || {};
              // Add product-specific metrics
              if (item.gmv !== undefined) existingItem.gmv = item.gmv;
              if (item.order_units !== undefined) existingItem.order_units = item.order_units;
              if (item.total_cvr !== undefined) existingItem.total_cvr = item.total_cvr;
              if (item.total_roas !== undefined) existingItem.total_roas = item.total_roas;
              dataMap.set(itemDate, existingItem);
            }
          });

          // Convert map back to array
          const data = Array.from(dataMap.values());
           // Separate data into current and previous periods based on user's selected date range
          // Previous: backendStartDate to userStartDate (exclusive)
          // Current: userStartDate to userEndDate (inclusive)
          const currentData = [];
          const previousData = [];

          data.forEach((item) => {
            const itemDate = item[dateField] || item.date;
            if (!itemDate) return;

            try {
              // Parse the date - handle both string and Date object formats
              const itemDateStr = typeof itemDate === 'string' ? itemDate : itemDate.toString();
              const itemDateObj = new Date(itemDateStr);
              
              // Set time to start of day for accurate comparison
              itemDateObj.setHours(0, 0, 0, 0);
              
              const userStartDateObjCheck = new Date(userStartDate);
              userStartDateObjCheck.setHours(0, 0, 0, 0);
              
              const userEndDateObjCheck = new Date(userEndDate);
              userEndDateObjCheck.setHours(23, 59, 59, 999);

              // Check if item belongs to current period (userStartDate to userEndDate)
              if (itemDateObj >= userStartDateObjCheck && itemDateObj <= userEndDateObjCheck) {
                currentData.push(item);
              }
              // Check if item belongs to previous period (before userStartDate)
              else if (itemDateObj < userStartDateObjCheck) {
                previousData.push(item);
              }
            } catch (e) {
              console.warn("Error parsing date:", itemDate, e);
            }
          });

          // Sort both arrays by date (data should already be ordered from backend, but ensure it)
          currentData.sort((a, b) => {
            const dateA = new Date(a[dateField] || a.date || 0);
            const dateB = new Date(b[dateField] || b.date || 0);
            return dateA - dateB;
          });

          previousData.sort((a, b) => {
            const dateA = new Date(a[dateField] || a.date || 0);
            const dateB = new Date(b[dateField] || b.date || 0);
            return dateA - dateB;
          });
          // Get column configs to map column keys to API response fields
          const dashboardColumns = getDashboardColumnsWithoutDimensions() || [];
          
          // Create a mapping of column keys to their API field names
          // Use dataIndex if available, otherwise use key
          const columnKeyToApiFieldMap = {};
          dashboardColumns.forEach((col) => {
            if (col.key && !col.isDimension) {
              // Use dataIndex if available, otherwise use key
              // For platform-specific fields, we may need to handle differently
              const apiField = col.dataIndex || col.key;
              columnKeyToApiFieldMap[col.key] = apiField;
            }
          });
          
          // Map data by column key - create series for each metric column
          const mappedTrendData = {};
          
          // Get all unique column keys from dashboard columns
          const metricColumnKeys = dashboardColumns
            .filter((col) => col.key && !col.isDimension)
            .map((col) => col.key);
          
          metricColumnKeys.forEach((columnKey) => {
            const apiField = columnKeyToApiFieldMap[columnKey] || columnKey;
            
            // Extract current period values for this metric
            const currentValues = currentData.map((item) => {
              // Try multiple field name variations
              const value = item[apiField] || 
                           item[apiField.toLowerCase()] || 
                           item[columnKey] || 
                           item[columnKey.toLowerCase()] || 
                           0;
              return value !== null && value !== undefined ? Number(value) : 0;
            });
              // Extract previous period values for this metric
              const previousValues = previousData.map((item) => {
                const value = item[apiField] || 
                             item[apiField.toLowerCase()] || 
                             item[columnKey] || 
                             item[columnKey.toLowerCase()] || 
                             0;
                return value !== null && value !== undefined ? Number(value) : 0;
              });

            // Format as ReactApexChart series format
            mappedTrendData[columnKey] = [
              {
                name: "Current",
                data: currentValues,
              },
              {
                name: "Previous",
                data: previousValues,
              },
            ];
          });

          setTrendDataSeries(mappedTrendData);
        }
      } catch (err) {
        console.error("Error fetching dashboard overview chart:", err);
        setError(err?.response?.data?.message || err?.message || "Unknown error occurred");
        toast.error(
          err?.response?.data?.message || err?.message || "Failed to fetch trend data series"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTrendDataSeries();
  }, [enabled, clientId, userPlatform, applyCalendar, clientAccountId]);

  return {
    chartData: trendDataSeries,
    loading,
    error,
  };
};